import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import {
  ImportControlModeTemplateErrorDto,
  ValidationErrorItem,
} from '@src/types/ControlModes';

interface ImportErrorDialogProps {
  open: boolean;
  onClose: () => void;
  error: ImportControlModeTemplateErrorDto | Error | null;
}

export default function ImportErrorDialog({
  open,
  onClose,
  error,
}: ImportErrorDialogProps): React.ReactElement {
  if (!error) {
    return <Dialog open={false} onClose={onClose} />;
  }

  const getErrorMessage = (): string => {
    if (error instanceof Error) {
      return error.message || 'Import failed';
    } else if ('message' in error) {
      return error.message || 'Import failed';
    }
    return 'Import failed';
  };

  const parseValidationErrors = (
    errors?: string | ValidationErrorItem[],
  ): ValidationErrorItem[] => {
    if (!errors) {
      return [];
    }

    if (typeof errors === 'string') {
      return errors
        .split(/,(?=An instance of)/)
        .map((err) => err.trim())
        .filter(Boolean)
        .map((errorMsg) => {
          const pathMatch = errorMsg.match(/at\s+(.*?)\s+/);
          const path = pathMatch ? pathMatch[1] : '';
          return {
            message: errorMsg,
            path: path,
          };
        });
    }

    return errors;
  };

  const validationErrors =
    'errors' in error ? parseValidationErrors(error.errors) : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>Import Failed</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant='body1' color='error' gutterBottom>
            {getErrorMessage()}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {validationErrors.length > 0 && (
          <>
            <Typography variant='h6' gutterBottom>
              Validation Errors
            </Typography>
            <List>
              {validationErrors.map((error, index) => (
                <ListItem
                  key={index}
                  sx={{ flexDirection: 'column', alignItems: 'flex-start' }}
                >
                  <Box sx={{ width: '100%' }}>
                    {/* Display the error message */}
                    <Typography
                      variant='body1'
                      component='p'
                      sx={{
                        color: 'error.main',
                        fontWeight: 'bold',
                      }}
                    >
                      {error.message}
                    </Typography>

                    {/* Display the path if available */}
                    {error.path && (
                      <Typography
                        variant='body2'
                        component='p'
                        sx={{
                          mt: 1,
                          ml: 2,
                          color: 'text.secondary',
                        }}
                      >
                        {error.path.startsWith('at ')
                          ? error.path
                          : `Path: ${error.path}`}
                      </Typography>
                    )}
                  </Box>
                  {index < validationErrors.length - 1 && (
                    <Divider sx={{ width: '100%', my: 1 }} />
                  )}
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary' variant='contained'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
